import { ref, computed } from 'vue';

/**
 * Levelling control-centre list source (PD-1180) — shared.
 *
 * Backs the control-centre table from the dedicated `document/levellingList`
 * endpoint, which sorts/filters server-side on COMPOSITE keys the doc-list Redis
 * index can't hold: the job level (lf_level / lf_track / lf_level_function_track
 * + the per-type tpa column), the evaluator (the per-type assigned role holder),
 * and evaluation status / method (document_lf_evaluation). Each returned row is a
 * normal doc-list row plus a `levelling` object with the resolved composites, so
 * the presentation layer reads one shape regardless of tenant/index mode.
 *
 * The caller owns the doc-list FILTER STATE (search, doc type, hierarchy,
 * classifiers, dates) and passes a `buildParams()` that returns those filter
 * params; this composable adds sort / paging / the levelling facets and fetches.
 *
 * @param {Object} options
 * @param {{post:Function}} options.http           axios-like client
 * @param {() => Object}    options.buildParams     current doc-list filter params
 * @param {number}          [options.pageSize=25]
 */
export function levellingList(options) {
  const { http, buildParams, pageSize = 25 } = options;

  const self = {
    rows: ref([]),
    totalCount: ref(0),
    counts: ref({ total: 0, levelled: 0, inProgress: 0, review: 0, invited: 0, sentBack: 0, notStarted: 0, toBeLevelled: 0 }),
    evaluatorOptions: ref([]), // distinct evaluator names (for the drawer filter)
    loading: ref(false),
    error: ref(null),

    // view state
    sortKey: ref('updated'),   // level | evaluator | status | method | updated | <index attr>
    sortDir: ref('desc'),      // asc | desc
    currentPage: ref(1),
    rowsPerPage: ref(pageSize),
    statusIncluded: ref([]),     // lifecycle keys
    methodIncluded: ref([]),     // 'classify' | 'evaluate'
    evaluatorIncluded: ref([]),  // evaluator names
    updatedFrom: ref(null),      // 'YYYY-MM-DD'
    updatedTo: ref(null),        // 'YYYY-MM-DD'
    anchorsOnly: ref(false),     // anchor roles (PD-1258): the "Anchors" toolbar chip
    /* The same data as an ORDINARY facet, so anchor status filters from the drawer
       like every other levelling column: 'ceiling' | 'anchor' | 'none'. Independent
       of the chip above — the chip is a one-click lens, this is a selection. */
    anchorIncluded: ref([]),
    /* CEO ceiling (PD-1258): isolate outcomes the server flagged as at or above the
       ceiling — the review queue the flag exists to make findable. */
    aboveCeilingOnly: ref(false),
  };

  self.totalPages = computed(() =>
    Math.max(1, Math.ceil(self.totalCount.value / self.rowsPerPage.value) || 1)
  );

  // Guards against out-of-order responses clobbering a newer request.
  let seq = 0;

  self.fetch = async () => {
    const mySeq = ++seq;
    self.loading.value = true;
    self.error.value = null;
    try {
      const params = {
        ...(buildParams ? buildParams() : {}),
        sortKey: self.sortKey.value,
        sortDir: self.sortDir.value,
        currentPage: self.currentPage.value,
        pageSize: self.rowsPerPage.value,
        statusIncluded: self.statusIncluded.value,
        methodIncluded: self.methodIncluded.value,
        evaluatorIncluded: self.evaluatorIncluded.value,
        anchorIncluded: self.anchorIncluded.value,
        aboveCeilingOnly: self.aboveCeilingOnly.value,
        updatedFrom: self.updatedFrom.value || null,
        updatedTo: self.updatedTo.value || null,
        anchorsOnly: self.anchorsOnly.value || undefined,
      };
      const resp = await http.post('document/levellingList', params);
      if (mySeq !== seq) return; // a newer fetch superseded this one
      const data = (resp && resp.data && resp.data.Data) || {};
      self.rows.value = Array.isArray(data.documents) ? data.documents : [];
      self.totalCount.value = Number(data.totalCount) || 0;
      if (data.counts) self.counts.value = { ...self.counts.value, ...data.counts };
      if (data.facets && Array.isArray(data.facets.evaluators)) self.evaluatorOptions.value = data.facets.evaluators;
    } catch (err) {
      if (mySeq !== seq) return;
      self.error.value = err;
      self.rows.value = [];
      self.totalCount.value = 0;
      console.error('Failed to fetch levelling list:', err);
    } finally {
      if (mySeq === seq) self.loading.value = false;
    }
  };

  self.setSort = (key, dir) => {
    self.sortKey.value = key;
    self.sortDir.value = dir === 'asc' ? 'asc' : 'desc';
    self.currentPage.value = 1;
    self.fetch();
  };

  self.setPage = (page) => {
    self.currentPage.value = page;
    self.fetch();
  };

  self.setPageSize = (size) => {
    self.rowsPerPage.value = size;
    self.currentPage.value = 1;
    self.fetch();
  };

  self.setStatusFilter = (keys) => {
    self.statusIncluded.value = Array.isArray(keys) ? keys : [];
    self.currentPage.value = 1;
    self.fetch();
  };

  self.setMethodFilter = (keys) => {
    self.methodIncluded.value = Array.isArray(keys) ? keys : [];
    self.currentPage.value = 1;
    self.fetch();
  };

  self.setEvaluatorFilter = (names) => {
    self.evaluatorIncluded.value = Array.isArray(names) ? names : [];
    self.currentPage.value = 1;
    self.fetch();
  };

  self.setAnchorFilter = (keys) => {
    self.anchorIncluded.value = Array.isArray(keys) ? keys : [];
    self.currentPage.value = 1;
    self.fetch();
  };

  self.setAboveCeilingOnly = (on) => {
    self.aboveCeilingOnly.value = !!on;
    self.currentPage.value = 1;
    self.fetch();
  };

  self.setAnchorsOnly = (on) => {
    self.anchorsOnly.value = !!on;
    self.currentPage.value = 1;
    self.fetch();
  };

  self.setUpdatedRange = (from, to) => {
    self.updatedFrom.value = from || null;
    self.updatedTo.value = to || null;
    self.currentPage.value = 1;
    self.fetch();
  };

  // Re-run for a filter-state change originating in the caller (search, doc type,
  // hierarchy, …). Resets to page 1 since the result set changed.
  self.reload = () => {
    self.currentPage.value = 1;
    return self.fetch();
  };

  return self;
}
